/**
 * Clients API client
 */

import { apiClient } from './client';
import type {
  Client,
  ClientCreate,
  ClientUpdate,
  ClientListResponse,
} from '../../types/client';

export interface ClientListParams {
  search?: string;
  active?: boolean;
  limit?: number;
  cursor?: string;
}

export interface DeactivateClientResponse {
  clientId: string;
  active: false;
  activeProjectsCount: number;
  message: string;
}

export const clientsApi = {
  /**
   * List clients with filters and cursor-based pagination
   */
  async list(params?: ClientListParams): Promise<ClientListResponse> {
    const queryParams: Record<string, any> = {};

    if (params?.search) queryParams.search = params.search;
    if (params?.active !== undefined) queryParams.active = params.active;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.cursor) queryParams.cursor = params.cursor;

    const response = await apiClient.get<ClientListResponse>('/api/v1/clients', {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * Get client by ID
   */
  async getById(clientId: string): Promise<Client> {
    const response = await apiClient.get<Client>(`/api/v1/clients/${clientId}`);
    return response.data;
  },

  /**
   * Create a new client
   */
  async create(data: ClientCreate): Promise<Client> {
    const response = await apiClient.post<Client>('/api/v1/clients', data);
    return response.data;
  },

  /**
   * Update client
   */
  async update(clientId: string, data: ClientUpdate): Promise<Client> {
    const response = await apiClient.patch<Client>(`/api/v1/clients/${clientId}`, data);
    return response.data;
  },

  /**
   * Deactivate (soft-delete) a client
   */
  async deactivate(
    clientId: string,
    confirmDeactivateWithProjects?: boolean
  ): Promise<DeactivateClientResponse> {
    const data = confirmDeactivateWithProjects
      ? { confirmDeactivateWithProjects: true }
      : undefined;
    const response = await apiClient.delete<DeactivateClientResponse>(
      `/api/v1/clients/${clientId}`,
      { data }
    );
    return response.data;
  },

  /**
   * Reactivate a previously deactivated client
   */
  async reactivate(clientId: string): Promise<Client> {
    const response = await apiClient.patch<Client>(`/api/v1/clients/${clientId}`, {
      active: true,
    });
    return response.data;
  },
};
